import React, { useMemo, useRef, useEffect, useCallback } from 'react';
import { YMaps, Map, Rectangle, Polygon, Placemark } from 'react-yandex-maps';
import { colorForRatio, HEAT_LAYER_OPACITY } from './heatColors';

const YANDEX_API_KEY = '71140458-f614-4101-ad58-f75c79f0f6fe';
const METERS_PER_DEGREE = 111320;
const DEFAULT_CENTER = [47.2357, 39.7015]; // Центр по умолчанию (Ростов-на-Дону), если точек нет
const DEFAULT_MIN_DELIVERY_TIME = 60;

const ZONE_Z_INDEX = 100;
const CELL_Z_INDEX = 300;
const FILIAL_Z_INDEX = 700;

export const buildCells = (points, cellMeters, metric) => {
    if (!points.length) return { cells: [], maxValue: 0 };

    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;

    for (const point of points) {
        if (point.lat < minLat) minLat = point.lat;
        if (point.lat > maxLat) maxLat = point.lat;
        if (point.lon < minLon) minLon = point.lon;
        if (point.lon > maxLon) maxLon = point.lon;
    }

    const latRef = (minLat + maxLat) / 2;
    const latStep = cellMeters / METERS_PER_DEGREE;
    const lonStep = cellMeters / (METERS_PER_DEGREE * Math.cos((latRef * Math.PI) / 180));

    const buckets = {};
    for (const point of points) {
        const i = Math.floor((point.lat - minLat) / latStep);
        const j = Math.floor((point.lon - minLon) / lonStep);
        const key = `${i}_${j}`;
        if (!buckets[key]) {
            buckets[key] = { i, j, count: 0, sum: 0 };
        }
        buckets[key].count += 1;
        buckets[key].sum += point.sum || 0;
    }

    let maxValue = 0;
    const cells = Object.keys(buckets).map((key) => {
        const bucket = buckets[key];
        const value = metric === 'revenue' ? bucket.sum : bucket.count;
        if (value > maxValue) maxValue = value;
        const lat1 = minLat + bucket.i * latStep;
        const lat2 = lat1 + latStep;
        const lon1 = minLon + bucket.j * lonStep;
        const lon2 = lon1 + lonStep;
        return { key, bounds: [[lat1, lon1], [lat2, lon2]], value, count: bucket.count, sum: bucket.sum };
    });

    return { cells, maxValue };
};

const normalizeZoneGeometry = (place) => {
    if (!Array.isArray(place)) return [];

    return place
        .map((ring) => {
            if (!Array.isArray(ring)) return [];
            return ring
                .map((coord) => {
                    if (!Array.isArray(coord) || coord.length < 2) return null;
                    const lat = Number(coord[1]);
                    const lon = Number(coord[0]);
                    if (isNaN(lat) || isNaN(lon)) return null;
                    return [lat, lon];
                })
                .filter(Boolean);
        })
        .filter((ring) => ring.length > 0);
};

const parseFilialCoord = (coord) => {
    if (typeof coord !== 'string') return null;
    const parts = coord.split(',');
    if (parts.length < 2) return null;
    const lat = Number(parts[0]);
    const lon = Number(parts[1]);
    if (isNaN(lat) || isNaN(lon)) return null;
    return [lat, lon];
};

const getFilialName = (filial) => {
    if (!filial) return '';
    const city = filial.city && filial.city.name ? filial.city.name : '';
    const street = [filial.street, filial.house].filter(Boolean).join(' ');
    return [city, street].filter(Boolean).join(', ');
};

const FILIAL_CAPTION = 'SUSHIBOX';

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const computeBounds = (coordinates) => {
    if (!coordinates.length) return null;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLon = Infinity;
    let maxLon = -Infinity;
    for (const coord of coordinates) {
        const lat = coord[0];
        const lon = coord[1];
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
        if (lon < minLon) minLon = lon;
        if (lon > maxLon) maxLon = lon;
    }
    const padLat = Math.max((maxLat - minLat) * 0.1, 0.003);
    const padLon = Math.max((maxLon - minLon) * 0.1, 0.003);
    return [[minLat - padLat, minLon - padLon], [maxLat + padLat, maxLon + padLon]];
};

const balloonContent = (cell) => (
    `<div>
        <div>Заказов: <b>${cell.count}</b></div>
        <div>Выручка: <b>${Math.round(cell.sum).toLocaleString('ru-RU')} ₽</b></div>
    </div>`
);

const zoneBalloonContent = (zone, filial) => {
    const zonePoint = zone.point && typeof zone.point === 'object' ? zone.point : null;
    const source = filial || zonePoint;
    const minDeliveryTime = source && source.minDeliveryTime
        ? Number(source.minDeliveryTime)
        : DEFAULT_MIN_DELIVERY_TIME;
    const deliveryTime = minDeliveryTime + (Number(zone.deliveryTime) || 0);
    const deliveryPrice = Number(zone.deliveryPrice) || 0;
    const minDeliveryPrice = Number(zone.minDeliveryPrice) || 0;
    const filialName = getFilialName(source);

    return `<div>
        ${filialName ? `<div>Филиал: <b>${escapeHtml(filialName)}</b></div>` : ''}
        ${zone.deliveryCode ? `<div>Код доставки: <b>${escapeHtml(zone.deliveryCode)}</b></div>` : ''}
        <div>Мин. сумма заказа: <b>${Number(zone.minDeliveryOrder) || 0} руб.</b></div>
        <div>Стоимость доставки: <b>${deliveryPrice > 0 ? `${deliveryPrice} руб.` : 'Бесплатно'}</b></div>
        ${minDeliveryPrice > 0 ? `<div>Бесплатная доставка от: <b>${minDeliveryPrice} руб.</b></div>` : ''}
        <div>Срок доставки: <b>${deliveryTime} мин.</b></div>
    </div>`;
};

const filialBalloonContent = (filial) => (
    `<div>
        ${filial.phone ? `<div>Телефон: <b>${escapeHtml(filial.phone)}</b></div>` : ''}
        ${filial.timeWork ? `<div>Работаем: <b>${escapeHtml(filial.timeWork)}</b></div>` : ''}
        <div>Мин. время доставки: <b>${Number(filial.minDeliveryTime) || DEFAULT_MIN_DELIVERY_TIME} мин.</b></div>
    </div>`
);

export default function HeatGrid({ points = [], zones = [], filials = [], fitZones = false, cellSize = 250, metric = 'count', height = 600 }) {
    const { cells, maxValue } = useMemo(
        () => buildCells(points, cellSize, metric),
        [points, cellSize, metric]
    );

    const zonesWithGeometry = useMemo(
        () => zones
            .map((zone, index) => ({ zone, index, geometry: normalizeZoneGeometry(zone.place) }))
            .filter((item) => item.geometry.length > 0),
        [zones]
    );

    const filialsWithCoord = useMemo(
        () => filials
            .map((filial) => ({ filial, coord: parseFilialCoord(filial.coord) }))
            .filter((item) => item.coord),
        [filials]
    );

    const filialsMap = useMemo(() => {
        const result = {};
        filials.forEach((filial) => {
            result[filial.id] = filial;
        });
        return result;
    }, [filials]);

    const bounds = useMemo(() => {
        if (points.length) return computeBounds(points.map((point) => [point.lat, point.lon]));
        if (!fitZones) return null;

        const zoneCoordinates = [];
        zonesWithGeometry.forEach((item) => {
            item.geometry.forEach((ring) => {
                ring.forEach((coord) => zoneCoordinates.push(coord));
            });
        });
        return computeBounds(zoneCoordinates);
    }, [points, zonesWithGeometry, fitZones]);
    const mapRef = useRef(null);

    const fitToPoints = useCallback(() => {
        if (mapRef.current && bounds) {
            mapRef.current.setBounds(bounds, { checkZoomRange: true, zoomMargin: 40 });
        }
    }, [bounds]);

    const applyLayerOpacity = useCallback(() => {
        const map = mapRef.current;
        if (!map || !map.panes) return;
        const pane = map.panes.get('areas');
        const el = pane && pane.getElement();
        if (el && el.style.opacity !== String(HEAT_LAYER_OPACITY)) {
            el.style.opacity = String(HEAT_LAYER_OPACITY);
        }
    }, []);

    useEffect(() => {
        fitToPoints();
    }, [fitToPoints]);

    useEffect(() => {
        if (mapRef.current && mapRef.current.container) {
            mapRef.current.container.fitToViewport();
            fitToPoints();
        }
    }, [height, fitToPoints]);

    return (
        <YMaps query={{ apikey: YANDEX_API_KEY }}>
            <Map
                width="100%"
                height={height}
                defaultState={{ center: DEFAULT_CENTER, zoom: 11 }}
                instanceRef={(instance) => {
                    if (instance && instance !== mapRef.current) {
                        mapRef.current = instance;
                        instance.geoObjects.events.add('add', applyLayerOpacity);
                        applyLayerOpacity();
                        fitToPoints();
                    }
                }}
                modules={['geoObject.addon.balloon']}
            >
                {zonesWithGeometry.map((item) => (
                    <Polygon
                        key={`zone_${item.zone.id || item.index}`}
                        geometry={item.geometry}
                        options={{
                            fillColor: '#b9b9b9',
                            fillOpacity: 0.55,
                            strokeColor: '#6f6f6f',
                            strokeWidth: 2,
                            strokeOpacity: 1,
                            zIndex: ZONE_Z_INDEX
                        }}
                        properties={{
                            balloonContentHeader: item.zone.name || 'Зона доставки',
                            balloonContent: zoneBalloonContent(
                                item.zone,
                                item.zone.point ? filialsMap[item.zone.point.id || item.zone.point] : null
                            )
                        }}
                        modules={['geoObject.addon.balloon']}
                    />
                ))}
                {cells.map((cell) => {
                    const ratio = maxValue > 0 ? cell.value / maxValue : 0;
                    return (
                        <Rectangle
                            key={cell.key}
                            geometry={cell.bounds}
                            options={{
                                fillColor: colorForRatio(ratio),
                                fillOpacity: 1,
                                strokeWidth: 0,
                                zIndex: CELL_Z_INDEX
                            }}
                            properties={{ balloonContent: balloonContent(cell) }}
                            modules={['geoObject.addon.balloon']}
                        />
                    );
                })}
                {filialsWithCoord.map((item) => {
                    const name = getFilialName(item.filial);
                    return (
                        <Placemark
                            key={`filial_${item.filial.id}`}
                            geometry={item.coord}
                            options={{
                                preset: 'islands#blueDotIconWithCaption',
                                zIndex: FILIAL_Z_INDEX
                            }}
                            properties={{
                                iconCaption: FILIAL_CAPTION,
                                balloonContentHeader: name,
                                balloonContent: filialBalloonContent(item.filial)
                            }}
                            modules={['geoObject.addon.balloon']}
                        />
                    );
                })}
            </Map>
        </YMaps>
    );
}
